import React, { useRef, useState, useEffect } from 'react';
import QrScanner from 'qr-scanner';
import Button from 'react-bootstrap/Button';
import Swal from 'sweetalert2';

interface QRReaderProps {
    onScan: (result: string) => void;
    onError?: (error: Error) => void;
}

export const QRReader = React.forwardRef<{ resetFileInput: () => void }, QRReaderProps>(({ onScan, onError }, ref) => {
    const [isScanning, setIsScanning] = useState<boolean>(false);
    const videoRef = useRef<HTMLVideoElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [qrScanner, setQrScanner] = useState<QrScanner | null>(null);
    const [fileInputKey, setFileInputKey] = useState<number>(0);

    // Función para limpiar el input de archivo
    const resetFileInput = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        // Cambiar la key para forzar re-renderizado del input
        setFileInputKey(prev => prev + 1);
    };

    // Exponer la función de reset al componente padre
    React.useImperativeHandle(ref, () => ({
        resetFileInput
    }));

    const startQrScanner = async () => {
        if (!videoRef.current) {
            console.error('Video element not found');
            return;
        }

        setIsScanning(true);

        try {
            if (qrScanner) {
                await qrScanner.start();
                return;
            }

            // Verificar si hay cámaras disponibles
            const hasCamera = await QrScanner.hasCamera();
            
            if (!hasCamera) {
                await Swal.fire({
                    icon: 'warning',
                    title: 'Cámara no disponible',
                    text: 'No se detectó ninguna cámara disponible en tu dispositivo',
                    confirmButtonText: 'Entendido'
                });
                setIsScanning(false);
                return;
            }

            // Solicitar permisos de cámara explícitamente
            try {
                await navigator.mediaDevices.getUserMedia({ video: true });
            } catch (permissionError) {
                console.error('Camera permission denied:', permissionError);
                await Swal.fire({
                    icon: 'error',
                    title: 'Permisos necesarios',
                    text: 'Se necesita acceso a la cámara para escanear códigos QR. Por favor, permite el acceso y vuelve a intentar.',
                    confirmButtonText: 'Entendido'
                });
                setIsScanning(false);
                return;
            }

            if (videoRef.current) {
                const scanner = new QrScanner(
                    videoRef.current,
                    (result: any) => {
                        onScan(result.data || result);
                        scanner.stop();
                        setIsScanning(false);
                    },
                    {
                        highlightScanRegion: true,
                        highlightCodeOutline: true,
                        returnDetailedScanResult: true,
                        onDecodeError: (error: any) => {
                            console.debug('Decode error (normal):', error);
                        }
                    }
                );

                setQrScanner(scanner);
                await scanner.start();
            }
        } catch (error) {
            console.error('Error starting QR scanner:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Error del escáner',
                text: 'Error al iniciar el escáner de QR. Verifica que tu navegador soporte cámaras y que hayas dado permisos.',
                confirmButtonText: 'Entendido'
            });
            setIsScanning(false);
            if (onError) {
                onError(error as Error);
            }
        }
    };

    const stopQrScanner = async () => {
        try {
            if (qrScanner) {
                await qrScanner.stop();
            }
        } catch (error) {
            console.error('Error stopping QR scanner:', error);
        } finally {
            setIsScanning(false);
        }
    };

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        QrScanner.scanImage(file)
            .then(result => {
                onScan(result);
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            })
            .catch(async error => {
                console.error('Error al escanear el código QR:', error);
                await Swal.fire({
                    icon: 'warning',
                    title: 'QR no encontrado',
                    text: 'No se pudo leer ningún código QR en la imagen seleccionada.',
                    confirmButtonText: 'Intentar de nuevo'
                });

                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
                if (onError) {
                    onError(error);
                }
            });
    };

    useEffect(() => {
        return () => {
            if (qrScanner) {
                try {
                    qrScanner.stop();
                    qrScanner.destroy?.();
                } catch (error) {
                    console.error('Error cleaning up QR scanner:', error);
                }
            }
        };
    }, [qrScanner]);

    return (
        <div className="qr-reader-section mb-3">
            <div className="">
                <div className="">
                    {/* Video element siempre presente pero oculto cuando no se escanea */}
                    <div className="video-container rounded mb-2" style={{ 
                        height: '240px', 
                        overflow: 'hidden',
                        display: isScanning ? 'block' : 'none'
                    }}>
                        <video 
                            ref={videoRef} 
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            autoPlay
                            playsInline
                            muted
                        ></video>
                    </div>

                    {isScanning ? (
                        <div className="text-center">
                            <Button
                                variant="danger"
                                size="lg"
                                className="mr-2"
                                onClick={stopQrScanner}
                            >
                                <i className="fas fa-stop mr-2"></i>
                                Detener Escáner
                            </Button>
                        </div>
                    ) : (
                        <div className="text-center">
                            <Button
                                variant="primary"
                                size="lg"
                                className="mr-3 mb-2"
                                onClick={startQrScanner}
                                style={{
                                    backgroundColor: '#eef1f1ff',
                                    border: 'none',
                                    color: '#4686c7'
                                }}
                            >
                                <i className="fas fa-barcode mr-2"></i> Usar escáner
                            </Button>

                            <label 
                                className="btn btn-lg mb-2"
                                style={{
                                    backgroundColor: '#eef1f1ff',
                                    border: 'none',
                                    color: '#4686c7',
                                    cursor: 'pointer'
                                }}
                            >
                                <i className="fas fa-image mr-2"></i> Subir foto
                                <input
                                    key={fileInputKey}
                                    type="file"
                                    accept="image/*"
                                    ref={fileInputRef}
                                    onChange={handleFileUpload}
                                    style={{ display: 'none' }}
                                />
                            </label>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});

QRReader.displayName = 'QRReader';

export default QRReader;
